/* eslint-disable react/jsx-pascal-case */

import { collection, doc, getDoc, getDocs, onSnapshot, query, setDoc, where } from "firebase/firestore";
import React, { useContext, useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom"; // To get the listing ID from the URL
import { useSwipeable } from "react-swipeable";
import config from "../../config";
import { EnvContext } from "../../context/EnvContext";
import { useModal } from "../../context/ModalContext";
import { UserContext } from "../../context/UserContext";
import COA from "../../elements/COA";
import FAQ from "../../elements/FAQ";
import Header from "../../elements/Header";
import HorizontalListing from '../../elements/HorizontalListing';
import ListingPriceFormater from "../../elements/ListingPriceFormater";
import NavigationSimple from "../../elements/NavigationSimple";
import PotencyCard from "../../elements/PotencyCard";
import ReviewList from "../../elements/ReviewList";
import ReviewsStaff from "../../elements/ReviewsStaff";
import ShopButton from "../../elements/ShopButton";
import { useFirestore } from "../../firebase/FirestoreContext"; // Use Firestore context
import addToCart from "../../functions/addToCart";
import createAnonymousUser from "../../functions/createAnonymousUser";
import { currentProductPrice } from "../../functions/currentProductPrice";
import analyticsHelper from "../../utils/analyticsHelper";

import PageLayout from "../../elements/PageLayout";
import isProductAvailable from "../../functions/isProductAvailable";
import QuantityButton from "../../elements/QuantityButton";
import ProductCard from "../../elements/ProductCard";
import SessionReplayContext from "../../context/SessionReplayContext";
import { AverageRating } from "../../elements/AverageRating";
import { ProductDescription } from "../../elements/ProductDescription";
import HeaderContext from "../../context/HeaderContext";
import noop from "../../functions/noop";
import StickyBuyButton from "../../elements/StickyBuyButton";
import getListingsCollection from "../../utils/getListingsCollection";

const breakpoints = [755]
const mq = breakpoints.map(bp => `@media (min-width: ${bp}px)`)

const Listing = ({ isDevMode }) => {
  //
  const environment = useContext(EnvContext);
  const { user } = useContext(UserContext);
  const { enableSessionReplay } = useContext(SessionReplayContext);
  const { showHeader, pulseCart } = useContext(HeaderContext);

  useEffect(() => {
    enableSessionReplay();
  }, [enableSessionReplay]);

  //
  const { listingID } = useParams(); // Get the listing ID from the URL
  const db = useFirestore(); // Get Firestore instance from context
  const { setCurrentModal } = useModal();
  const [isMounted, setIsMounted] = useState(false);
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0); // Track currently selected image index
  const [viewImage, setViewImage] = useState(null); // Currently viewed image in modal
  const [quantity, setQuantity] = useState(0); // Track quantity
  const [localQuantity, setLocalQuantity] = useState(0); // local state for quantity
  const [disabledQuantity, setDisabledQuantity] = useState(false); // Disable quantity input
  const [priceInfo, setPriceInfo] = useState({});
  const [similarListingsState, setSimilarListingsState] = useState([]);
  //
  const [selectedQuantity, setSelectedQuantity] = useState(1); // Default to 1
  const [showStickyBuyButton, setShowStickyBuyButton] = useState(false);
  const addToOrderRef = useRef(null);

  // environment
  const collectionPath = environment === "staging" ? config.environment.staging.collectionPath : config.environment.production.collectionPath;

  // Use a separate handler for menu swipes but disable it if swiping on an image
  const menuHandlers = useSwipeable({
    onSwipedRight: () => { }, // Intentionally left empty to prevent menu from opening
    onSwipedLeft: () => { }, // Intentionally left empty to prevent menu from closing
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
    disabled: false, // Disable menu swipe when swiping on image
  });

  // Function to check if the product is already in the cart
  const checkProductInCart = useCallback(async (listingID) => {
    if (!user) {
      console.error("User is not authenticated.");
      return;
    }

    if (!listingID) {
      setSelectedQuantity(1);
      return;
    }

    try {
      const cartQuery = query(collection(db, `${collectionPath}Carts/CartsList/DataBase`), where("user_id", "==", user.uid), where("status", "==", "ordering"));
      const cartSnap = await getDocs(cartQuery);

      if (!cartSnap.empty) {
        const cartID = cartSnap.docs[0]?.id;
        const itemRef = doc(db, `${collectionPath}Carts/CartsList/DataBase/${cartID}/Items`, listingID);

        const itemSnap = await getDoc(itemRef);
        if (itemSnap.exists()) {
          const cartQuantity = itemSnap.data().quantity;
          setQuantity(cartQuantity);
          setSelectedQuantity(cartQuantity);
          setLocalQuantity(cartQuantity);
        } else {
          setSelectedQuantity(1);
          // Restarts quantity if it is not in the cart
          setQuantity(0);
          setLocalQuantity(0);
        }
      } else {
        setSelectedQuantity(1);
        // Restarts quantity if there is no cart
        setQuantity(0);
        setLocalQuantity(0);
      }
    } catch (error) {
      console.error("Error checking product in cart:", error);
      setSelectedQuantity(1);
      // Restarts quantity in case of error
      setQuantity(0);
      setLocalQuantity(0);
    }
  }, [collectionPath, db, user]);

  const handleAddToCart = async () => {
    // Check if the selected quantity exceeds the sooperdeal limit
    if (data?.sooperdeal_limit_per_order && selectedQuantity > data.sooperdeal_limit_per_order) {
      window.alert(`You have reached the maximum limit of ${data.sooperdeal_limit_per_order} for this product.`);
      return;
    }

    let currentUser = user || (await createAnonymousUser());

    // Check if the item is already in the cart
    const cartQuery = query(
      collection(db, `${collectionPath}Carts/CartsList/DataBase`),
      where("user_id", "==", currentUser.uid),
      where("status", "==", "ordering")
    );
    const cartSnap = await getDocs(cartQuery);

    showHeader();

    if (!cartSnap.empty) {
      const cartID = cartSnap.docs[0]?.id;
      const itemRef = doc(db, `${collectionPath}Carts/CartsList/DataBase/${cartID}/Items`, listingID);
      const itemSnap = await getDoc(itemRef);

      if (itemSnap.exists()) {
        // If the item is already in the cart, update the quantity
        const existingQuantity = itemSnap.data().quantity;

        if (selectedQuantity === existingQuantity) {
          // If selected quantity is equal to existing quantity, increment by 1
          const newQuantity = existingQuantity + 1;
          await addToCart(listingID, currentUser, db, 1, collectionPath); // Increment by 1
          setQuantity(newQuantity);
          setSelectedQuantity(newQuantity);
        } else {
          // If selected quantity is different from existing quantity, update to selected quantity
          const newQuantity = selectedQuantity;
          await addToCart(listingID, currentUser, db, 1, collectionPath);
          setQuantity(newQuantity);
          setSelectedQuantity(newQuantity);
        }
      } else {
        // If the item is not in the cart, add it with the selected quantity
        await addToCart(listingID, currentUser, db, 1, collectionPath);
        setQuantity(selectedQuantity);
        setSelectedQuantity(selectedQuantity);
      }
    } else {
      // If the cart is empty, add the item with the selected quantity
      await addToCart(listingID, currentUser, db, 1, collectionPath);
      setQuantity(1);
      setSelectedQuantity(1);
    }
    pulseCart();
  };

  const changeProductQuantity = (newQuantity) => {
    if (newQuantity < 0) {
      setLocalQuantity(0);
      return;
    }
    if (
      data?.sooperdeal_limit_per_order &&
      newQuantity > data.sooperdeal_limit_per_order
    ) {

      alert(`You reached the maximum limit of ${data.sooperdeal_limit_per_order}.`);
      setLocalQuantity(data.sooperdeal_limit_per_order);
      return;
    }
    if (data.listing_stock_amount && newQuantity > data.listing_stock_amount) {
      alert(`Only ${data.listing_stock_amount} items available in stock.`);
      setLocalQuantity(data.listing_stock_amount);
      return;
    }
    setLocalQuantity(newQuantity);
  }

  // recently viewed
  const updateRecentlyViewedProducts = useCallback(async () => {
    if (!user) return; // Ensure the user is logged in

    try {
      const recentlyViewedRef = doc(db, `Users/UsersList/DataBase/${user.uid}/RecentlyViewedProducts`, listingID);

      await setDoc(recentlyViewedRef, {
        date: new Date(),
        environment: environment,
      });
    } catch (error) {
      console.error("Error updating recently viewed products:", error);
    }
  }, [db, environment, listingID, user])


  useEffect(() => {
    setLocalQuantity(quantity);
  }, [quantity, data]);

  useEffect(() => {
    if (localQuantity === quantity) return;
    showHeader();
    const handler = setTimeout(async () => {
      setDisabledQuantity(true);
      if (!user || !listingID) return;
      const diff = localQuantity - quantity;
      if (diff !== 0) {
        await addToCart(listingID, user, db, diff, collectionPath);
        pulseCart();
        setQuantity(localQuantity);
      }
    }, 500);
    return () => {
      clearTimeout(handler)
      setDisabledQuantity(false);
    };
  }, [localQuantity, quantity, user, listingID, db, collectionPath, showHeader]);

  const handleImageClick = (image, index) => {
    setViewImage(image);
    setSelectedIndex(index);
  };

  const fetchPriceInfo = async (productData) => {
    try {
      const result = await currentProductPrice(productData);
      setPriceInfo(result); // Store the price info
    } catch (error) {
      console.error("Error fetching product price:", error);
    }
  };

  // Fetch prices for similar listings
  const fetchPricesForSimilarListings = async (listings) => {
    const listingsWithPrices = await Promise.all(
      listings.map(async (listing) => {
        const { finalPrice } = await currentProductPrice(listing);
        return {
          ...listing,
          price: finalPrice,
        };
      })
    );
    setSimilarListingsState(listingsWithPrices); // Update the state with listings that include prices
  };

  useEffect(() => {
    const { ref } = menuHandlers;
    ref(document);

    return () => {
      ref(null); // Clean up swipe handlers
    };
  }, [menuHandlers]);

  useEffect(() => {
    document.body.classList.add("no-scroll");
    document.body.style.overflow = "hidden";
    if (viewImage) {
      document.body.style.overflow = "hidden"; // Disable scrolling
    } else {
      document.body.style.overflow = "auto"; // Re-enable scrolling
    }
    return () => {
      document.body.style.overflow = "auto"; // Cleanup in case of unmount
    };
  }, [viewImage]);

  //Updates product quantity 
  useEffect(() => {
    if (user && listingID) {
      checkProductInCart(data?.product_id);
    } else {
      setQuantity(0);
      setLocalQuantity(0);
      setSelectedQuantity(1);
    }
  }, [checkProductInCart, listingID, user]);

  // Fetch product data and check if the product is in the cart
  useEffect(() => {
    if (!db) {
      console.error("Firestore instance is not initialized.");
      return;
    }

    const fetchSimilarListings = async (product_category) => {
      const listings = [];
      const queryRelatedSnapshot = getListingsCollection(db, where("product_category", "==", product_category));
      const relatedSnapshot = await getDocs(queryRelatedSnapshot);

      relatedSnapshot.forEach((doc) => {
        if (doc.id !== listingID && doc.data().listing_stock_amount !== 0) {
          listings.push(doc.data());
        }
      });

      setSimilarListingsState(listings);
      await fetchPricesForSimilarListings(listings);
    };

    const fetchTaggedListings = async (tags) => {
      const queryRelatedSnapshot = getListingsCollection(
        db,
        where("tags", "array-contains-any", tags),
        where("listing_stock_amount", "!=", 0),
      );

      const relatedSnapshot = await getDocs(queryRelatedSnapshot);

      const listings = relatedSnapshot.docs
        .filter((doc) => doc.id !== listingID)
        .map((doc) => doc.data());

      setSimilarListingsState(listings);
      await fetchPricesForSimilarListings(listings);
    }

    const unsubscribe = onSnapshot(
      doc(db, `${collectionPath}Listings/ListingsList/DataBase`, listingID),
      async (doc) => {
        if (doc.exists()) {
          const listingData = doc.data();

          if (listingData.tags?.includes('Kava')) {
            fetchTaggedListings(listingData.tags);
          } else {
            fetchSimilarListings(listingData?.product_category);
          }

          setData(listingData);
          fetchPriceInfo(listingData);
          setLoading(false);
          if (user) {
            checkProductInCart(listingID);
            await updateRecentlyViewedProducts();
          }
        } else {
          console.error("No such document!");
          setLoading(false);
          navigate("/notfound", { replace: true });
        }
      },
      (error) => {
        console.error("Error fetching document: ", error);
        setLoading(false);
        navigate("/notfound", { replace: true });
      }
    );

    return () => unsubscribe(); // Clean up the subscription on unmount
  }, [db, listingID, isDevMode, user, collectionPath, checkProductInCart, updateRecentlyViewedProducts, navigate]);

  // Analytics
  useEffect(() => {
    if (isMounted) return noop;


    if (data) {
      setIsMounted(true);
      (async () => {
        const price = await currentProductPrice(data);

        analyticsHelper.productViewed({
          ProductName: data.product_name,
          ProductID: data.product_id,
          SKU: data.id,
          Categories: [],
          ImageURL: data.images,
          URL: `${window.location.origin}/Listing/${data.seller_product_id}`,
          Brand: data.brand_name,
          Price: price.finalPrice,
        });
      })();
    }
  }, [isMounted, data]);


  useEffect(() => {
    function checkAddToOrderVisibility() {
      if (!addToOrderRef.current) return;
      const rect = addToOrderRef.current.getBoundingClientRect();
      const isVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      setShowStickyBuyButton(!isVisible);
    }
    window.addEventListener('scroll', checkAddToOrderVisibility, { passive: true });
    window.addEventListener('resize', checkAddToOrderVisibility);

    checkAddToOrderVisibility();
    return () => {
      window.removeEventListener('scroll', checkAddToOrderVisibility);
      window.removeEventListener('resize', checkAddToOrderVisibility);
    };
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!addToOrderRef.current) return;
      const rect = addToOrderRef.current.getBoundingClientRect();
      const isVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      setShowStickyBuyButton(!isVisible);
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [loading]);

  if (loading || !data) {
    return <div className="loading">Loading...</div>; // Loading indicator
  }

  // calculate rating and initial rating
  let rating = parseFloat(data?.rating?.rating) || 0;
  let votes = parseFloat(data?.rating?.votes) || 0;
  let initialRating = parseFloat(data?.rating_initial?.rating) || 0;
  let initialVoting = parseFloat(data?.rating_initial?.voting) || 0;

  // if all is 0
  if (votes === 0 && initialVoting === 0) {
    rating = initialRating;
    votes = initialVoting;
  } else {
    rating = parseFloat((rating + initialRating * initialVoting) / (initialVoting + votes)).toFixed(1);
    votes = votes + initialVoting;
  }

  if (loading || !data) {
    return <div className="loading">Loading...</div>;
  }


  return (
    <PageLayout analyticsView="product" analyticsTitle="Product Page" description={data?.product_description} imageUrl={data?.images?.[0]} title={data?.product_name} showNewsletterModal url={`https://getfullproof.com/Listing/${listingID}`}>

      <Header search={false} />

      <div css={styles.container}>
        <div css={{ height: 20 }} />

        <NavigationSimple />

        <div css={{ borderBottom: "1px solid black", marginBottom: 15 }} />

        <div css={styles.listingHeader}>
          {/* Brand and Voting */}
          <div css={{ ...styles.brand.container }}>
            <div css={styles.brand.name}>{data?.brand_name || ""}</div>
            <div css={{}}>
              <div css={{}}>
                {/* Review counter */}
                <AverageRating rating={rating} votes={votes} onClick={() => {
                  // if user is logged in, then go to page review, if not show popup modal
                  if (!user) {
                    setCurrentModal("signUptoReview");
                  } else {
                    window.location.href = `/review-create/${data?.id}`;
                  }
                }} />
              </div>
            </div>
          </div>

          {/* Product Name */}
          <div css={styles.name.container}>
            <h1 css={styles.productName}>{data?.product_name || ""}</h1>

            {data?.sooperdeal_limit_per_order && data.sooperdeal_limit_per_order > 0 && (
              <div
                css={{
                  marginTop: 10,
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                  alignContent: "flex-start",
                  alignSelf: "flex-start",
                }}
              >
                <div
                  css={{
                    padding: "3px 8px",
                    border: "1px solid silver",
                    borderRadius: "4px",
                    fontSize: 12,
                  }}
                >
                  Limit {data.sooperdeal_limit_per_order} per order
                </div>
              </div>
            )}
          </div>
        </div>

        <div css={{ height: 20 }} />
        <ProductCard handleImageClick={handleImageClick} selectedIndex={selectedIndex} data={data} priceInfo={priceInfo} collectionPath={collectionPath} />

        <div css={{ height: 10 }} />
        {isProductAvailable(data) ? (
          /* Add to order elements */
          <div ref={addToOrderRef} css={{ width: "100%", margin: "0px auto", padding: "0 10px" }}>
            {localQuantity === 0 ? (
              <ShopButton
                text={"Add to order"}
                background={"#007aff"}
                containerStyles={styles.shop.button}
                buttonStyle={styles.shop.text}
                arrow={false}
                textColor={"white"}
                onClick={handleAddToCart}
              />
            ) : (
              <div css={{ display: "flex", justifyContent: "center" }}>
                <QuantityButton
                  initialQuantity={localQuantity}
                  disabled={disabledQuantity}
                  outlinedColor={"rgb(0, 122, 255)"}
                  width="380px"
                  onChange={
                    (newQuantity) => {
                      if (newQuantity < 0) {
                        setLocalQuantity(0);
                        return;
                      }
                      if (
                        data?.sooperdeal_limit_per_order &&
                        newQuantity > data.sooperdeal_limit_per_order
                      ) {

                        alert(`You reached the maximum limit of ${data.sooperdeal_limit_per_order}.`);
                        setLocalQuantity(data.sooperdeal_limit_per_order);
                        return;
                      }
                      if (data.listing_stock_amount && newQuantity > data.listing_stock_amount) {
                        alert(`Only ${data.listing_stock_amount} items available in stock.`);
                        setLocalQuantity(data.listing_stock_amount);
                        return;
                      }
                      setLocalQuantity(newQuantity);
                    }
                  }
                />
              </div>
            )}
          </div>
        ) : (
          <ShopButton containerStyles={styles.shop.button}
            buttonStyle={styles.shop.text} text={"Out of Stock"} background={"gray"} arrow={false} textColor={"white"} disabled />
        )}
        <div css={{ height: 10 }} />

        <HorizontalListing
          title="You might also like"
          products={similarListingsState}
          limit={10}
          displayShowAllArrow={false}
          displayShowAllButton={false}
        />

        <div css={{ height: 10 }} />

        <div css={{ ...styles.productDetails }}>
          {/** */}
          <div css={{ borderTop: ".5px solid #B3B3B3" }} />

          <div css={{}}>
            <div css={{ ...styles.title, padding: "20px 0 20px 0" }}>Product Details</div>
            <div css={{ ...styles?.infoRow?.label, marginBottom: 10 }}>Description</div>
            {data?.product_description ? (
              <ProductDescription description={data?.product_description} />
            ) : (
              "No description available."
            )}
            <div
              css={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                paddingTop: "20px",
              }}
            >
              <div css={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <div css={styles?.infoRow?.container}>
                  <div css={styles?.infoRow?.label}>Net Weight</div>
                  <div css={styles?.infoRow?.value}>
                    {data?.net_weight} {data?.weight_units}
                  </div>
                </div>
                <div css={styles?.infoRow?.container}>
                  <div css={styles?.infoRow?.label}>Serving Size</div>
                  <div css={styles?.infoRow?.value}>{data?.serving_size || "N/A"}</div>
                </div>

                <div css={styles?.infoRow?.container}>
                  <div css={styles?.infoRow?.label}>Total Servings</div>
                  <div css={styles?.infoRow?.value}>{data?.number_servings || "N/A"}</div>
                </div>

                <div css={styles?.infoRow?.container}>
                  <div css={styles?.infoRow?.label}>Dose / Serving</div>
                  <div css={styles?.infoRow?.value}>{data?.dose_per_serving || "N/A"}</div>
                </div>
              </div>
            </div>

            <ShopButton
              text={"Rate This Product"}
              outlined
              theme={"dark"}
              textColor={"black"}
              arrow={false}
              padding={"10px 10px"}
              containerStyles={styles.shop.button}
              buttonStyle={styles.shop.text}
              onClick={() => {
                // if user is logged in, then go to page review, if not show popup modal
                if (!user) {
                  setCurrentModal("signUptoReview");
                } else {
                  window.location.href = `/review-create/${data?.id}`;
                }
              }}
            />
          </div>

          {/** */}
          <div css={{ height: 20 }} />
          <div css={{ borderTop: ".5px solid #B3B3B3" }} />

          <ReviewsStaff product={data} />

          <div css={{ height: 20 }} />
          <PotencyCard />
          <div css={{ height: 20 }} />
          <div css={{ borderTop: ".5px solid #B3B3B3" }} />
          <div css={{ height: 20 }} />
          <COA />
          <div css={{ height: 20 }} />
          {/** */}
          <div css={{ borderTop: ".5px solid #B3B3B3" }} />

          {/** */}
          <ReviewList product={data} />
        </div>
        {/** */}
        <FAQ />

      </div>
      {showStickyBuyButton && (
        <StickyBuyButton
          quantity={localQuantity}
          priceInfo={priceInfo}
          data={data}
          handleAddToCart={handleAddToCart}
          onChange={
            (newQuantity) => {
              changeProductQuantity(newQuantity);
            }
          }
        />
      )}
    </PageLayout>
  );
};

const styles = {
  container: {
    backgroundColor: "white",
    maxWidth: "1140px",
    margin: "0 auto",
  },
  title: {
    fontSize: "1.3rem",
    fontWeight: "800",
    display: "flex",
    color: "black",
  },
  /**  */
  keyIngredients: {
    display: "flex",
    flexDirection: "column",
    fontSize: ".9rem",
    fontWeight: 300,
    color: "black",
    marginTop: "5px",
    [mq[0]]: {
      textAlign: "right",
      fontSize: "1.15rem",
    },
  },
  /* like and share */
  shop: {
    button: {
      maxWidth: "380px",
      margin: "0px auto",
    },
    text: {
      fontSize: "1rem",
      lineHeight: "1rem",
      [mq[0]]: {
        fontSize: "1.25rem",
        lineHeight: "1.25rem",
      },
    }
  },
  label: {
    fontSize: "12px",
    fontWeight: "100",
    marginBottom: "2px",
    color: "gray",
  },
  //
  rating: {
    marginTop: "10px",
  },
  //
  brand: {
    container: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "0px 10px 0px 10px",
      fontSize: ".9rem",
      [mq[0]]: {
        fontSize: "1.1rem",
      },
    },
    name: {
      color: "black",
      fontWeight: 600,
      fontSize: "1rem",
      [mq[0]]: {
        fontSize: "1.25rem",
      },
    },
    shareContainer: {
      display: "flex",
      alignItems: "center",
      cursor: "pointer",
    },
  },
  //
  imageGallery: {
    display: "flex",
    flexDirection: "row",
    height: "210px",
    whiteSpace: "nowrap",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    marginBottom: "20px",
    maxWidth: "1000px",
    overflowX: "scroll",
    overflowY: "hidden",
    width: "100%",
  },
  ".imageGallery::-webkit-scrollbar": {
    display: "none",
  },
  image: {
    paddingLeft: "10px",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  },
  thumbnail: {
    border: "1px solid #ddd",
    width: "80%",
    height: "100%",

    objectFit: "cover",
    cursor: "pointer",
    marginRight: "5px",
  },
  //
  name: {
    container: {
      color: "black",
      padding: "0 10px",
    },
  },
  productName: {
    color: "black",
    fontSize: "1rem",
    [mq[0]]: {
      fontSize: "1.25rem",
    },
  },
  //
  productDetails: {
    padding: "0 10px ",
    fontSize: "14px",
    [mq[0]]: {
      fontSize: "1.1rem",
    },
  },
  additionalDetails: {
    padding: "0 10px ",
    fontSize: "14px",
    paddingBottom: "100px",
  },
  infoRow: {
    container: {
      display: "flex",
      flexDirection: "row",
      paddingBottom: 10,
    },
    label: {
      display: "flex",
      width: "50%",
      fontWeight: "600",
    },
    value: {
      display: "flex",
      width: "50%",
      paddingLeft: "10px",
    },
  },

  quantityControls: {
    display: "flex",
    alignItems: "center",
  },
  quantityButton: {
    backgroundColor: "#ddd",
    border: "none",
    padding: "10px 20px",
    cursor: "pointer",
    fontSize: "20px",
  },
  quantity: {
    margin: "0 10px",
    fontSize: "16px",
  },
};



export default Listing;
